/**
 * SessionGate — nothing in the console renders until we know who is signed in.
 *
 * Bridge owns no auth UI: an unauthenticated visitor goes to the platform auth
 * app (signup → email verification → login → profile) and comes back with the
 * session cookie set. Same delegation the nexus/salon consoles use.
 *
 * When USE_PLATFORM_AUTH is off this is a pass-through, so local dev against a
 * bridge API running with AUTH_DEV_BYPASS=true is unchanged.
 */
import React from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../theme';
import { USE_PLATFORM_AUTH } from '../api/config';
import { fetchSession, goToLogin, goToCompleteProfile, goToNotInPlan, PlatformSession } from '../api/session';
import { isNotInPlan } from '../api/planGate';

type State =
  | { phase: 'checking' }
  | { phase: 'ready'; session: PlatformSession | null }
  | { phase: 'error'; message: string };

const SessionContext = React.createContext<PlatformSession | null>(null);

/** The signed-in platform session, or null in dev-bypass mode. */
export function useSession(): PlatformSession | null {
  return React.useContext(SessionContext);
}

export default function SessionGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<State>(
    USE_PLATFORM_AUTH ? { phase: 'checking' } : { phase: 'ready', session: null },
  );

  const check = React.useCallback(() => {
    setState({ phase: 'checking' });
    fetchSession()
      .then((session) => {
        // Signed in, but this org's plan does not include the Bridge website
        // (SK Fleet / Salon use Bridge underneath without selling it). No list
        // = open. Stays on "checking" while the browser leaves.
        if (session && isNotInPlan(session.subscribed_products)) {
          try {
            goToNotInPlan();
            return;
          } catch {
            // Could not build the URL — keep today's behaviour and open Bridge.
          }
        }
        setState({ phase: 'ready', session });
      })
      .catch((err: Error) => setState({ phase: 'error', message: err.message }));
  }, []);

  React.useEffect(() => {
    if (USE_PLATFORM_AUTH) check();
  }, [check]);

  if (state.phase === 'checking') {
    return (
      <Centered>
        <ActivityIndicator size="small" color={colors.ink3 as string} />
        <Text style={styles.muted}>Checking your session…</Text>
      </Centered>
    );
  }

  // Platform unreachable: say so and offer a retry. Never fall through to the
  // console — that would look signed-in with every call about to 401.
  if (state.phase === 'error') {
    return (
      <Centered>
        <Text style={styles.title}>Can't verify your session</Text>
        <Text style={styles.body}>{state.message}</Text>
        <Action label="Try again" onPress={check} />
      </Centered>
    );
  }

  if (USE_PLATFORM_AUTH && !state.session) {
    return (
      <Centered>
        <Text style={styles.title}>Sign in to SK Bridge</Text>
        <Text style={styles.body}>
          Bridge uses your Softknack account. You'll come straight back here after signing in.
        </Text>
        <Action label="Continue to sign in" onPress={goToLogin} primary />
      </Centered>
    );
  }

  // Verified and signed in, but the profile step was never finished — the
  // platform owns that screen, so send them to it rather than half-loading here.
  if (state.session && !state.session.profileComplete) {
    return (
      <Centered>
        <Text style={styles.title}>One more step</Text>
        <Text style={styles.body}>Tell us your name and company to finish setting up your account.</Text>
        <Action label="Complete your profile" onPress={goToCompleteProfile} primary />
      </Centered>
    );
  }

  return <SessionContext.Provider value={state.session}>{children}</SessionContext.Provider>;
}

function Centered({ children }: { children: React.ReactNode }) {
  return <View style={styles.center}>{children}</View>;
}

function Action({ label, onPress, primary }: { label: string; onPress: () => void; primary?: boolean }) {
  return (
    <Pressable onPress={onPress} style={[styles.btn, primary ? styles.btnPrimary : null]}>
      <Text style={[styles.btnText, primary ? styles.btnTextPrimary : null]}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: 40,
    backgroundColor: colors.paper,
  },
  title: { fontFamily: fontFamily.sans, fontSize: fontSize.xl, fontWeight: fontWeight.semibold, color: colors.ink },
  body: { fontFamily: fontFamily.sans, fontSize: fontSize.md, color: colors.ink3, textAlign: 'center', maxWidth: 420 },
  muted: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink3 },
  btn: {
    marginTop: 6,
    height: 38,
    paddingHorizontal: 16,
    borderRadius: radii.r2,
    borderWidth: 1,
    borderColor: colors.lineStrong,
    backgroundColor: colors.card,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  } as any,
  btnPrimary: { backgroundColor: colors.accent, borderColor: colors.accent },
  btnText: { fontFamily: fontFamily.sans, fontSize: fontSize.md, fontWeight: fontWeight.medium, color: colors.ink2 },
  btnTextPrimary: { color: '#fff' },
});
